import { useState } from 'react';
import { Menu, X, User, Plus } from 'lucide-react';
import logo from '../../assets/claraViewMedia/claraview-logo-1.avif';
import logoLight from '../../assets/claraview-v2/signup-logo.webp';

/*
 * The dashboard chrome: desktop rail at md+, hamburger top bar + slide-in drawer below it.
 * Ported from the Figma Make DashboardLayout. Owns only its own drawer state — active-page
 * tracking and routing stay with the caller, so this stays a presentational shell.
 *
 * navItems: [{ id, label, icon, route, badge? }]  — badge renders only when truthy
 * accountItem: same shape; pinned to the bottom of both shells and to the top bar's avatar.
 * onNewSimulation: optional; renders the New Simulation CTA above the nav in both shells.
 */

const NavButton = ({ item, dark, isActive, onSelect }) => {
    const Icon = item.icon;
    return (
        <button
            type="button"
            onClick={() => onSelect(item)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${dark
                ? (isActive ? 'bg-white/[0.08] text-white' : 'text-white/[0.45] hover:bg-white/5 hover:text-white')
                : (isActive ? 'bg-[#f6f6f4] text-[#2D2D2D]' : 'text-[#6B6B6B] hover:bg-[#FAFAF9] hover:text-[#2D2D2D]')
                }`}
        >
            <Icon className="w-5 h-5 shrink-0" />
            <span className={dark ? 'text-[15px]' : 'text-sm'}>{item.label}</span>
            {item.badge ? (
                <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[#D92D20] px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                    {item.badge}
                </span>
            ) : null}
        </button>
    );
};

const DashboardShell = ({ navItems, accountItem, activePage, onNavigate, onNewSimulation, children }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);

    const select = (item) => {
        onNavigate(item);
        setDrawerOpen(false);
    };

    const startSimulation = () => {
        setDrawerOpen(false);
        onNewSimulation();
    };

    const renderNav = (dark) => (
        <>
            {onNewSimulation && (
                <div className="px-4 pt-4">
                    <button
                        type="button"
                        onClick={startSimulation}
                        className={`w-full h-11 flex items-center justify-center gap-2 rounded-lg transition-colors ${dark
                            ? 'bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.12] text-white'
                            : 'bg-[#2D2D2D] hover:bg-[#1A1A1A] active:bg-[#111111] text-white'
                            }`}
                    >
                        <Plus className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
                        <span className="font-dashboard text-[10px] uppercase tracking-[0.5px]">New Simulation</span>
                    </button>
                </div>
            )}
            <nav className={`flex-1 p-4 space-y-1${dark ? ' overflow-y-auto' : ''}`}>
                {navItems.map((item) => (
                    <NavButton
                        key={item.id}
                        item={item}
                        dark={dark}
                        isActive={activePage === item.id}
                        onSelect={select}
                    />
                ))}
            </nav>
            <div className={`border-t p-4 ${dark ? 'border-white/[0.07]' : 'border-[#E8E6E3]'}`}>
                <NavButton
                    item={accountItem}
                    dark={dark}
                    isActive={activePage === accountItem.id}
                    onSelect={select}
                />
            </div>
        </>
    );

    return (
        <div className="flex h-dvh bg-[#F7F7F8] font-[Inter]">

            {/* Desktop rail */}
            <aside className="hidden md:flex w-64 bg-white border-r border-[#E8E6E3] flex-col shrink-0">
                <div className="p-6 border-b border-[#E8E6E3]">
                    <img src={logo} alt="Claraview" className="h-8" />
                </div>
                {renderNav(false)}
            </aside>

            {/* Drawer scrim */}
            {drawerOpen && (
                <div
                    className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
                    onClick={() => setDrawerOpen(false)}
                />
            )}

            {/* Mobile slide-in drawer */}
            <div
                className={`md:hidden fixed top-0 left-0 h-full w-[280px] z-50 bg-[#161612] flex flex-col transform transition-transform duration-300 ease-out ${drawerOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.07]">
                    <img src={logoLight} alt="Claraview" className="h-[18px] w-auto opacity-80 object-contain" />
                    <button
                        type="button"
                        onClick={() => setDrawerOpen(false)}
                        className="text-white/40 hover:text-white transition-colors p-1"
                        aria-label="Close menu"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {renderNav(true)}
            </div>

            {/* min-w-0 lets wide tables scroll inside themselves instead of pushing the page */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

                <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-[#E8E6E3] shrink-0">
                    <button
                        type="button"
                        onClick={() => setDrawerOpen(true)}
                        className="text-[#6B6B6B] hover:text-[#2D2D2D] transition-colors p-1"
                        aria-label="Open menu"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <img src={logo} alt="Claraview" className="h-6 w-auto object-contain" />
                    <button
                        type="button"
                        onClick={() => onNavigate(accountItem)}
                        className={`p-1 rounded-full transition-colors ${activePage === accountItem.id ? 'text-[#2D2D2D]' : 'text-[#6B6B6B] hover:text-[#2D2D2D]'
                            }`}
                        aria-label={accountItem.label}
                    >
                        <User className="w-5 h-5" />
                    </button>
                </header>

                <main className="flex-1 overflow-auto">{children}</main>
            </div>
        </div>
    );
};

export default DashboardShell;
